import { c as _c } from "react/compiler-runtime";
import { Button } from '@systemeio/components';
import { useUpdateFunnelStepQuery } from 'modules/funnels/funnel/hooks/use-update-funnel-step-query';
import { type ReactNode } from 'react';
import { IconTile } from 'shared/components/icon-tile';
import { ViewportSwitch } from 'shared/components/viewport-switch';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { twMerge } from 'tailwind-merge';
import { SplitTestComponentTypeEnum, SplitTestComponentTypeTitles } from '../../enums/split-tests-component-type-enum';
import { FunnelStepId } from '../../types/funnel-step-interface';
import { SplitTestOriginalImage } from './split-test-original-image';
import { SplitTestVariantImage } from './split-test-variant-image';
interface SplitTestItemTitleProps {
  className?: string;
  funnelStep?: {
    id: FunnelStepId;
    name: string;
  };
  isCurrent?: boolean;
  title?: ReactNode;
}

/**
 * The step name is the way into the step: the card used to navigate through an icon button beside
 * it, which left the name looking like plain text next to an affordance with no label of its own.
 */
const SplitTestItemTitle = t0 => {
  const $ = _c(14);
  const {
    className,
    funnelStep,
    isCurrent,
    title
  } = t0;
  const {
    updateFunnelStepQuery
  } = useUpdateFunnelStepQuery();
  if (funnelStep && !isCurrent) {
    let t1;
    if ($[0] !== className) {
      t1 = twMerge("max-w-full whitespace-normal", className);
      $[0] = className;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== funnelStep.id || $[3] !== updateFunnelStepQuery) {
      t2 = () => updateFunnelStepQuery(funnelStep.id);
      $[2] = funnelStep.id;
      $[3] = updateFunnelStepQuery;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== funnelStep.name || $[6] !== t1 || $[7] !== t2) {
      t3 = <Button className={t1} variant="link" size="sm" onClick={t2}>{funnelStep.name}</Button>;
      $[5] = funnelStep.name;
      $[6] = t1;
      $[7] = t2;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    return t3;
  }
  let t1;
  if ($[9] !== className) {
    t1 = twMerge("text-base font-medium", className);
    $[9] = className;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  const t2 = funnelStep ? funnelStep.name : title;
  let t3;
  if ($[11] !== t1 || $[12] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
};
type SplitTestItemProps = {
  action?: ReactNode;
  description?: ReactNode;
  type: SplitTestComponentTypeEnum;
} & ({
  funnelStep?: {
    id: FunnelStepId;
    name: string;
  };
  title?: never;
  isCurrent?: boolean;
} | {
  title?: ReactNode;
  funnelStep?: never;
  isCurrent?: never;
});
const SplitTestItem = t0 => {
  const $ = _c(66);
  const {
    action,
    description,
    type,
    funnelStep,
    title,
    isCurrent
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const isOriginal = type === SplitTestComponentTypeEnum.original;
  const Illustration = isOriginal ? SplitTestOriginalImage : SplitTestVariantImage;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = twMerge("border-border-100 bg-bg-primary tablet:min-h-80 tablet:flex-col tablet:items-stretch tablet:p-5 flex min-w-0 flex-1 items-center gap-5 rounded-xl border p-4 text-left");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== Illustration) {
    t2 = <Illustration className="size-20 shrink-0" />;
    $[1] = Illustration;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = isOriginal ? "primary" : "lightGreen";
  const t4 = isOriginal ? "A" : "B";
  let t5;
  if ($[3] !== t3 || $[4] !== t4) {
    t5 = <IconTile tone={t3} size="m">{t4}</IconTile>;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = SplitTestComponentTypeTitles[type];
  let t7;
  if ($[6] !== t || $[7] !== t6) {
    t7 = t(t6);
    $[6] = t;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t7) {
    t8 = <span className="text-base font-medium">{t7}</span>;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t5 || $[12] !== t8) {
    t9 = <div className="flex items-center gap-2">{t5}{t8}</div>;
    $[11] = t5;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== funnelStep || $[15] !== isCurrent || $[16] !== title) {
    t10 = <SplitTestItemTitle className="text-start" funnelStep={funnelStep} isCurrent={isCurrent} title={title} />;
    $[14] = funnelStep;
    $[15] = isCurrent;
    $[16] = title;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t9) {
    t11 = <div className="flex min-w-0 flex-col justify-between gap-3">{t9}{t10}</div>;
    $[18] = t10;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11 || $[22] !== t2) {
    t12 = <div className="flex items-center gap-3">{t2}{t11}</div>;
    $[21] = t11;
    $[22] = t2;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== description) {
    t13 = description && <div className="text-text-tertiary text-sm">{description}</div>;
    $[24] = description;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== action) {
    t14 = action && <div className="[&_button]:w-full">{action}</div>;
    $[26] = action;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t12 || $[29] !== t13 || $[30] !== t14) {
    t15 = <div className="flex w-full flex-col gap-3">{t12}{t13}{t14}</div>;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const t16 = isOriginal ? "primary" : "lightGreen";
  const t17 = isOriginal ? "A" : "B";
  let t18;
  if ($[32] !== t16 || $[33] !== t17) {
    t18 = <IconTile tone={t16} size="m">{t17}</IconTile>;
    $[32] = t16;
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  const t19 = SplitTestComponentTypeTitles[type];
  let t20;
  if ($[35] !== t || $[36] !== t19) {
    t20 = t(t19);
    $[35] = t;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t20) {
    t21 = <span className="text-base font-medium">{t20}</span>;
    $[38] = t20;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== t18 || $[41] !== t21) {
    t22 = <div className="flex items-center gap-3">{t18}{t21}</div>;
    $[40] = t18;
    $[41] = t21;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== Illustration) {
    t23 = <Illustration className="mx-auto shrink-0" />;
    $[43] = Illustration;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== funnelStep || $[46] !== isCurrent || $[47] !== title) {
    t24 = <SplitTestItemTitle className="text-center" funnelStep={funnelStep} isCurrent={isCurrent} title={title} />;
    $[45] = funnelStep;
    $[46] = isCurrent;
    $[47] = title;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== description) {
    t25 = description && <div className="text-text-tertiary text-center text-sm">{description}</div>;
    $[49] = description;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== t24 || $[52] !== t25) {
    t26 = <div className="flex min-w-0 flex-1 flex-col items-center gap-1">{t24}{t25}</div>;
    $[51] = t24;
    $[52] = t25;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== t23 || $[55] !== t26) {
    t27 = <div className="flex flex-col gap-2.5">{t23}{t26}</div>;
    $[54] = t23;
    $[55] = t26;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  let t28;
  if ($[57] !== action) {
    t28 = action && <div className="self-center">{action}</div>;
    $[57] = action;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  let t29;
  if ($[59] !== t22 || $[60] !== t27 || $[61] !== t28) {
    t29 = <>{t22}{t27}{t28}</>;
    $[59] = t22;
    $[60] = t27;
    $[61] = t28;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  let t30;
  if ($[63] !== t15 || $[64] !== t29) {
    t30 = <div className={t1}><ViewportSwitch at="tablet" below={t15} from={t29} /></div>;
    $[63] = t15;
    $[64] = t29;
    $[65] = t30;
  } else {
    t30 = $[65];
  }
  return t30;
};
export default SplitTestItem;